import { ChevronRightIcon } from '@heroicons/react/20/solid'
import { ColumnDef } from '@tanstack/react-table'
import { ReactNode, useState } from 'react'
import Combo from 'app/components/ui/combo/combo'
import ComboMultiSelect from 'app/components/ui/combo/combo-multi-select'
import ComboMultiSelectAsync from 'app/components/ui/combo/combo-multi-select-async'
import { fetchBlockoutNotes } from 'app/graphql/blockoutNotes'
import { Tooltip } from 'app/components/Tooltip'
import type { AIAgentConfigurationType } from 'app/types/entities'
import type { AppointmentTypeType } from 'app/types/entities'
import { getCategoriesForAppointmentType } from 'app/table-columns/appointment-types'
import { OptionType } from 'types/OptionType'

const patientTypeOptions: OptionType[] = [
  { id: `new`, name: `New Patient`, value: `New Patient` },
  { id: `existing`, name: `Current Patient`, value: `Existing Patient` },
  { id: `any`, name: `Any`, value: undefined },
]

const statusOptions: OptionType[] = [
  { id: `active`, name: `Enabled`, value: `active` },
  { id: `inactive`, name: `Disabled`, value: `inactive` },
]

const blockSchedulingOptions: OptionType[] = [
  { id: `enabled`, name: `Enabled`, value: `enabled` },
  { id: `disabled`, name: `Disabled`, value: `disabled` },
]

const doubleBookingOptions: OptionType[] = [
  { id: `enabled`, name: `Enabled`, value: `enabled` },
  { id: `disabled`, name: `Disabled`, value: `disabled` },
]

const enabledProductsOptions: OptionType[] = [
  { id: `scheduler`, name: `Scheduler`, value: `scheduler` },
  { id: `agent`, name: `Agent`, value: `agent` },
]

const categoryOptions = [
  { name: `General`, id: `general`, value: `general` },
  { name: `Emergency`, id: `emergency`, value: `emergency` },
  { name: `Consultation`, id: `consultation`, value: `consultation` },
  { name: `Limited Exam`, id: `limited exam`, value: `limited exam` },
]

export const appointmentTypesUnifiedColumns = ({
  onEditAppointmentTypeClick,
  onFieldChange,
  allowDoubleBooking,
  excludableAppointmentTypes,
  agentConfig,
  isCarestack,
}: {
  onEditAppointmentTypeClick: (appointmentType: AppointmentTypeType) => void
  onFieldChange: (id: string, field: string, value: any) => void
  allowDoubleBooking?: boolean
  excludableAppointmentTypes?: { id: string; name: string }[]
  agentConfig: AIAgentConfigurationType | undefined
  isCarestack?: boolean
}): ColumnDef<AppointmentTypeType, any>[] => {
  const columns: ColumnDef<AppointmentTypeType, any>[] = [
    {
      header: `PMS Name`,
      id: `PMS Name`,
      accessorFn: (row) => row.displayName,
      meta: {
        ControlledComponent: (row) => {
          const [tooltipContent, setTooltipContent] = useState<ReactNode>(undefined)
          return (
            <div className="flex items-center justify-between px-2">
              <Tooltip
                className="cursor-pointer overflow-hidden overflow-ellipsis whitespace-nowrap"
                placement="bottom"
                message={tooltipContent}
                onMouseOver={(event) => {
                  if (
                    event.currentTarget instanceof HTMLElement &&
                    event.currentTarget.scrollWidth > event.currentTarget.clientWidth
                  ) {
                    setTooltipContent(row?.displayName)
                  } else {
                    setTooltipContent(undefined)
                  }
                }}
              >
                <div className="flex flex-col gap-1">{row?.displayName}</div>
              </Tooltip>
              <button
                onClick={() => onEditAppointmentTypeClick(row!)}
                className="mr-2 hidden h-8 items-center rounded-lg border border-strong bg-surface-subtle p-2 text-ui-xs text-strong group-hover:flex"
              >
                <p>Edit</p>
                <ChevronRightIcon height={16} width={16} />
              </button>
            </div>
          )
        },
        defaultVisible: true,
        pinned: true,
        sortKey: `displayName.keyword`,
        group: `General`,
      },
      enableHiding: false,
    },
    {
      header: `Display Name`,
      id: `Display Name`,
      accessorFn: (row) => row.alias,
      cell: (props) => <span>{props.getValue() || `—`}</span>,
      meta: {
        defaultVisible: true,
        group: `General`,
      },
      enableHiding: true,
    },
    {
      header: `Status`,
      id: `Status`,
      accessorFn: (row) => (row.activeScheduler ? `active` : `inactive`),
      meta: {
        ControlledComponent: (row) => {
          const current = row?.activeScheduler ? statusOptions[0] : statusOptions[1]
          return (
            <Combo
              options={statusOptions}
              value={current}
              onChange={(val) => onFieldChange(row!.id!, `activeScheduler`, val?.value === `active`)}
              className="px-2"
              placeholder=""
              inputClassName="!h-full !border-0 !shadow-none !py-0 !pl-0 !pr-5 cursor-pointer"
              chevronClassName="hidden group-hover/cell:block"
            />
          )
        },
        defaultVisible: true,
        group: `General`,
      },
      enableHiding: true,
    },
    {
      header: `Patient Type`,
      id: `Patient Type`,
      accessorFn: (row) => row.patientType,
      meta: {
        ControlledComponent: (row) => {
          const current = patientTypeOptions.find((o) => o.value === row?.patientType)
          return (
            <Combo
              options={patientTypeOptions}
              value={current}
              onChange={(val) => onFieldChange(row!.id!, `patientType`, val?.value)}
              className="px-2"
              placeholder=""
              inputClassName="!h-full !border-0 !shadow-none !py-0 !pl-0 !pr-5 cursor-pointer"
              chevronClassName="hidden group-hover/cell:block"
            />
          )
        },
        defaultVisible: true,
        group: `General`,
      },
      enableHiding: true,
    },
    {
      header: `Block Scheduling`,
      id: `Block Scheduling`,
      accessorFn: (row) => row.blockScheduling,
      meta: {
        ControlledComponent: (row) => {
          const current = row?.blockScheduling ? blockSchedulingOptions[0] : blockSchedulingOptions[1]
          return (
            <Combo
              options={blockSchedulingOptions}
              value={current}
              onChange={(val) => onFieldChange(row!.id!, `blockScheduling`, val?.value === `enabled`)}
              className="px-2"
              placeholder=""
              inputClassName="!h-full !border-0 !shadow-none !py-0 !pl-0 !pr-5 cursor-pointer"
              chevronClassName="hidden group-hover/cell:block"
            />
          )
        },
        defaultVisible: true,
        group: `Scheduling`,
      },
      enableHiding: true,
    },
    {
      header: `Block Notes`,
      id: `Block Notes`,
      accessorFn: (row) => row.blockSchedulingNotes,
      meta: {
        ControlledComponent: (row) => {
          if (!row?.blockScheduling) {
            return <span className="px-2 text-minimal">N/A</span>
          }
          return (
            <ComboMultiSelectAsync
              name={`blockSchedulingNotes-${row?.id}`}
              fetcher={fetchBlockoutNotes}
              additionalParams={{ locationId: row?.kollaConsumerId || `` }}
              optionsMapper={(res: string[]) =>
                (res || []).map((name) => ({
                  id: name.startsWith(`note:`) ? name : `note:${name}`,
                  name: name.startsWith(`note:`) ? name.slice(5) : name,
                  value: name.startsWith(`note:`) ? name : `note:${name}`,
                }))
              }
              values={row?.blockSchedulingNotes ?? []}
              queryValues={false}
              onChangeHandler={(options) => {
                onFieldChange(
                  row!.id!,
                  `blockSchedulingNotes`,
                  options.map((o) => o.value),
                )
              }}
              className="px-2"
              inputClassName="!h-full !border-0 !shadow-none !py-0 !pl-0 !pr-5 cursor-pointer"
              placeholder="-"
            />
          )
        },
        defaultVisible: true,
        group: `Scheduling`,
      },
      enableHiding: true,
    },
    {
      header: `Exclude Blockouts`,
      id: `Exclude Blockouts`,
      accessorFn: (row) => row.excludedBlockouts,
      meta: {
        ControlledComponent: (row) => {
          if (row?.blockScheduling) {
            return <span className="px-2 text-minimal">N/A</span>
          }
          const blockoutOptions: OptionType[] = (excludableAppointmentTypes ?? []).map((apt) => ({
            id: apt.id,
            name: apt.name,
            value: apt.id,
          }))
          return (
            <ComboMultiSelectAsync
              name={`excludeBlockouts-${row?.id}`}
              fetcher={fetchBlockoutNotes}
              additionalParams={{ locationId: row?.kollaConsumerId || `` }}
              additionalChoices={blockoutOptions}
              optionsMapper={(res: string[]) =>
                (res || []).map((name) => ({
                  id: name.startsWith(`note:`) ? name : `note:${name}`,
                  name: name.startsWith(`note:`) ? name.slice(5) : name,
                  value: name.startsWith(`note:`) ? name : `note:${name}`,
                }))
              }
              values={row?.excludedBlockouts ?? []}
              queryValues={false}
              onChangeHandler={(options) => {
                onFieldChange(
                  row!.id!,
                  `excludedBlockouts`,
                  options.map((o) => o.value),
                )
              }}
              className="px-2"
              inputClassName="!h-full !border-0 !shadow-none !py-0 !pl-0 !pr-5 cursor-pointer"
              placeholder="-"
            />
          )
        },
        defaultVisible: true,
        group: `Scheduling`,
      },
      enableHiding: true,
    },
    {
      header: `Double Booking`,
      id: `Double Booking`,
      accessorFn: (row) => row.doubleBookingExclusion,
      meta: {
        ControlledComponent: (row) => {
          if (!allowDoubleBooking) {
            return <span className="px-2 text-minimal">Disabled</span>
          }
          const current = row?.doubleBookingExclusion ? doubleBookingOptions[1] : doubleBookingOptions[0]
          return (
            <Combo
              options={doubleBookingOptions}
              value={current}
              onChange={(val) => onFieldChange(row!.id!, `doubleBookingExclusion`, val?.value === `disabled`)}
              className="px-2"
              placeholder=""
              inputClassName="!h-full !border-0 !shadow-none !py-0 !pl-0 !pr-5 cursor-pointer"
              chevronClassName="hidden group-hover/cell:block"
            />
          )
        },
        defaultVisible: true,
        group: `Scheduling`,
      },
      enableHiding: true,
    },
    {
      header: `Enabled Products`,
      id: `Enabled Products`,
      accessorFn: (row) => {
        const products: string[] = []
        if (row.activeScheduler) products.push(`Scheduler`)
        if (row.activeAgent) products.push(`Agent`)
        return products.join(`, `)
      },
      meta: {
        ControlledComponent: (row) => {
          const selected: OptionType[] = []
          if (row?.activeScheduler) selected.push(enabledProductsOptions[0])
          if (row?.activeAgent) selected.push(enabledProductsOptions[1])
          return (
            <ComboMultiSelect
              name={`enabledProducts-${row?.id}`}
              options={enabledProductsOptions}
              values={selected}
              onChange={(options) => {
                const schedulerActive = options.some((o) => o.value === `scheduler`)
                const agentActive = options.some((o) => o.value === `agent`)
                if (schedulerActive !== !!row?.activeScheduler) {
                  onFieldChange(row!.id!, `activeScheduler`, schedulerActive)
                }
                if (agentActive !== !!row?.activeAgent) {
                  onFieldChange(row!.id!, `activeAgent`, agentActive)
                }
              }}
              className="px-2"
              inputClassName="!h-full !border-0 !shadow-none !py-0 !pl-0 !pr-5 cursor-pointer"
              placeholder="None"
            />
          )
        },
        defaultVisible: true,
        group: `Products`,
      },
      enableHiding: true,
    },
    {
      header: `Appointment Category`,
      id: `Appointment Category`,
      meta: {
        ControlledComponent: (row) => {
          const selected = row?.id ? getCategoriesForAppointmentType(agentConfig, row.id) : []
          const label = selected
            .map((id) => categoryOptions.find((o) => o.id === id)?.name)
            .filter(Boolean)
            .join(`, `)
          return <span className="px-2 text-ui-xs text-strong">{label || `—`}</span>
        },
        defaultVisible: true,
        group: `Agent`,
      },
      enableHiding: true,
    },
    {
      header: `Duration`,
      id: `Duration`,
      accessorFn: (row) => row.appointmentLength,
      cell: (props) => <span>{props.getValue()} Minutes</span>,
      meta: {
        defaultVisible: true,
        group: `General`,
      },
      enableHiding: true,
    },
    {
      header: `Procedure Codes`,
      id: `Procedure Codes`,
      accessorFn: (row) => row.procedureCodes?.join(`, `),
      meta: {
        defaultVisible: true,
        group: `General`,
      },
      enableHiding: true,
    },
  ]

  if (isCarestack) {
    const hiddenIds = new Set([
      `Block Scheduling`,
      `Exclude Blockouts`,
      `Double Booking`,
      `Duration`,
      `Procedure Codes`,
    ])
    return columns.filter((col) => !hiddenIds.has(col.id!))
  }

  return columns
}
